import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ConfirmDialog, PageHeader, btnPrimary } from "@/components/app/common";
import { RichText } from "@/components/app/RichText";

const STARTERS = ["Su quale marca conviene investire?", "Su quale canale conviene investire?", "Quali prodotti dovrei spingere?", "Dove posso tagliare i costi?", "Come sta andando questo mese rispetto al precedente?", "Quali budget rischio di sforare?"];

async function streamReply(cid, testo, onDelta, onDone) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/conversazioni/${cid}/messaggi`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
    body: JSON.stringify({ testo }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Errore di invio");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.error) throw new Error(ev.error);
      if (ev.delta) onDelta(ev.delta);
      if (ev.done && onDone) onDone(ev);
    }
  }
}

function ConvList({ convs, active, onSelect, onNew, onDelete }) {
  return (
    <aside className="card-soft p-3 flex flex-col min-h-0 lg:h-full" data-testid="conversazioni-list">
      <Button onClick={onNew} className={`${btnPrimary} w-full mb-3`} data-testid="new-chat-button"><Plus className="h-4 w-4 mr-1" />Nuova chat</Button>
      <div className="overflow-y-auto space-y-1 max-h-48 lg:max-h-none">
        {!convs.length && <p className="text-xs text-[#756B64] p-3">Nessuna conversazione salvata.</p>}
        {convs.map((c, i) => (
          <div key={c.id} data-testid={`conversazione-item-${i}`} onClick={() => onSelect(c.id)}
            className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer text-sm transition-colors ${active === c.id ? "bg-[#E8DED6]" : "hover:bg-[#F4EFEA]"}`}>
            <MessageCircle className="h-4 w-4 shrink-0 text-[#8C6D62]" />
            <div className="min-w-0 flex-1">
              <div className="truncate">{c.titolo}</div>
              <div className="text-[10px] text-[#756B64]">{fmtDate(c.updated_at)}</div>
            </div>
            <button data-testid={`delete-conversazione-${i}`} onClick={(e) => { e.stopPropagation(); onDelete(c); }} className="p-1 rounded-lg opacity-60 hover:opacity-100 hover:bg-[#E07A5F]/15 text-[#B4553D]"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </aside>
  );
}

const Bubble = ({ m, i }) => (
  <div data-testid={`chat-message-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[88%] sm:max-w-[78%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[#8C6D62] text-white rounded-br-md" : "bg-[#FAF7F5] border border-[#EAE3DC] text-[#2C2623] rounded-bl-md"}`}>
      {m.role === "user" ? <p className="whitespace-pre-wrap">{m.content}</p> : m.content ? <RichText text={m.content} /> : <Loader2 className="h-4 w-4 animate-spin text-[#8C6D62]" />}
      {m.non_verificati?.length > 0 && <p data-testid={`chat-non-verificati-${i}`} className="mt-2 text-[11px] text-[#A2513C]">Attenzione: questi numeri non corrispondono ai dati dell'app, verificali: {m.non_verificati.join(", ")}</p>}
    </div>
  </div>
);

export default function ConsulenteChat() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [del, setDel] = useState(null);
  const endRef = useRef();

  const loadConvs = useCallback(() => api.get("/ai/conversazioni").then((r) => setConvs(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const select = async (id) => {
    setActive(id);
    try { const { data } = await api.get(`/ai/conversazioni/${id}`); setMsgs(data.messaggi); } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (text) => {
    const testo = (text ?? input).trim();
    if (!testo || sending) return;
    setSending(true);
    setInput("");
    let cid = active;
    try {
      if (!cid) { cid = (await api.post("/ai/conversazioni")).data.id; setActive(cid); }
      setMsgs((m) => [...m, { role: "user", content: testo }, { role: "assistant", content: "" }]);
      await streamReply(cid, testo, (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }),
        (ev) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], non_verificati: ev.non_verificati || [] }; return c; }));
    } catch (e) {
      toast.error(e.message || "Errore");
      setMsgs((m) => m.filter((x, idx) => !(idx >= m.length - 2 && (x.content === "" || x.content === testo))));
    } finally {
      setSending(false);
      loadConvs();
    }
  };

  const remove = async () => {
    try { await api.delete(`/ai/conversazioni/${del.id}`); if (del.id === active) { setActive(null); setMsgs([]); } loadConvs(); toast.success("Conversazione eliminata"); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div data-testid="chat-page">
      <PageHeader overline="Consulente AI" title="Chiedi al consulente" subtitle="Fai domande sui tuoi numeri: il consulente risponde usando i dati aggiornati della tua attività." />
      <div className="grid lg:grid-cols-[260px_1fr] gap-5 lg:h-[calc(100vh-260px)] lg:min-h-[520px]">
        <ConvList convs={convs} active={active} onSelect={select} onNew={() => { setActive(null); setMsgs([]); }} onDelete={setDel} />
        <section className="card-soft flex flex-col min-h-[480px] lg:min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="chat-messages">
            {!msgs.length ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <span className="h-14 w-14 rounded-full bg-[#E8C5C8]/50 grid place-items-center mb-4"><Sparkles className="h-6 w-6 text-[#8C6D62]" /></span>
                <h3 className="text-xl font-medium">Come posso aiutarti?</h3>
                <p className="text-sm text-[#756B64] mt-1 mb-6">Prova con una di queste domande:</p>
                <div className="flex flex-wrap gap-2 justify-center max-w-xl">
                  {STARTERS.map((q, i) => (
                    <button key={q} data-testid={`starter-question-${i}`} onClick={() => send(q)} disabled={sending} className="text-sm px-4 py-2 rounded-full border border-[#EAE3DC] bg-white hover:bg-[#F4EFEA] transition-colors">{q}</button>
                  ))}
                </div>
              </div>
            ) : msgs.map((m, i) => <Bubble key={m.id || i} m={m} i={i} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-[#EAE3DC] p-3 sm:p-4 flex gap-2 items-end">
            <textarea data-testid="chat-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Scrivi una domanda sui tuoi dati…" className="flex-1 resize-none max-h-32 rounded-xl border border-[#EAE3DC] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C6D62]/30" />
            <Button type="submit" disabled={sending || !input.trim()} className="rounded-full h-10 w-10 p-0 bg-[#8C6D62] hover:bg-[#77594E] shrink-0" data-testid="chat-send-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </section>
      </div>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la conversazione?" text={del?.titolo} onConfirm={remove} />
    </div>
  );
}
